// One Inbox card that can be swiped, on a phone (app-feel plan, builder B).
//
// Right = « Traité » (green reveal), left = « Ignorer » (grey reveal). The
// rules — the 24 px edge, the ~10 px axis lock, the threshold — are swipe.ts;
// this only follows the finger and calls `onSwipe`, which goes through the
// Inbox's own inboxAct mutation (optimistic « gone » + the 6 s undo).
//
// ⚠️ `touch-action: pan-y`: the browser keeps the vertical scroll (smooth, its
// own), and hands sideways moves to us. Nothing here calls preventDefault.
import { useRef, useState, type ReactNode } from 'react';
import { useT } from '@/lib/i18n';
import { dragOffset, inEdge, lockAxis, swipeOutcome, swipeThreshold, type Axis, type SwipeAction, type SwipeAllow } from './swipe';

type Drag = { x0: number; y0: number; axis: Axis | null; edge: boolean };

export function SwipeRow({ children, allow, onSwipe }: {
  children: ReactNode;
  allow: SwipeAllow;
  onSwipe: (action: SwipeAction) => void;
}) {
  const { t } = useT();
  const ref = useRef<HTMLDivElement | null>(null);
  const drag = useRef<Drag | null>(null);
  const [dx, setDx] = useState(0);
  const [phase, setPhase] = useState<'idle' | 'drag' | 'settle' | 'out'>('idle');

  const width = () => ref.current?.getBoundingClientRect().width || 0;

  const onTouchStart = (e: React.TouchEvent) => {
    const p = e.touches[0];
    if (!p || phase === 'out') return;
    drag.current = { x0: p.clientX, y0: p.clientY, axis: null, edge: inEdge(p.clientX) };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const d = drag.current;
    const p = e.touches[0];
    if (!d || !p || d.edge) return;
    const mx = p.clientX - d.x0;
    const my = p.clientY - d.y0;
    if (!d.axis) d.axis = lockAxis(mx, my);
    if (d.axis !== 'x') return;
    setPhase('drag');
    setDx(dragOffset(mx, allow));
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.edge || d.axis !== 'x') return;
    const p = e.changedTouches[0];
    const mx = p ? p.clientX - d.x0 : dx;
    const outcome = swipeOutcome(mx, width(), allow);
    if (!outcome) { setPhase('settle'); setDx(0); return; }
    // Off the screen in the swipe's direction, then the action — the undo line takes over.
    setPhase('out');
    setDx((mx > 0 ? 1 : -1) * Math.max(width(), 400));
    window.setTimeout(() => onSwipe(outcome), 180);
  };

  const past = Math.abs(dx) >= swipeThreshold(width());
  const right = dx > 0;
  return (
    <div ref={ref} data-swipe-row className="relative overflow-hidden" style={{ touchAction: 'pan-y' }}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={() => { drag.current = null; setPhase('settle'); setDx(0); }}>
      {/* The reveal under the card: what letting go will do — words, not colour alone. */}
      {dx !== 0 && (
        <div aria-hidden className={`absolute inset-0 flex items-center px-5 text-[13px] font-semibold transition-colors duration-150 ${
          right
            ? `justify-start ${past ? 'bg-success dark:bg-status-done-solid text-white' : 'bg-success/25 text-[#3F4C37] dark:text-status-done'}`
            : `justify-end ${past ? 'bg-text-secondary dark:bg-[#6B5D4F] text-white' : 'bg-border-subtle text-text-secondary'}`
        }`}>
          {right ? `✓ ${t('Traité')}` : `✗ ${t('Ignorer')}`}
        </div>
      )}
      <div
        className={`relative bg-bg-card ${phase === 'drag' ? '' : 'transition-transform duration-200 ease-out motion-reduce:transition-none'}`}
        style={{ transform: dx ? `translate3d(${dx}px,0,0)` : undefined }}
      >
        {children}
      </div>
    </div>
  );
}
